import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { IWorkHoursItem } from "../../../models/workHours/workHoursItem";
import {
    getUpToDateNormalWorkHours,
    getUpToDateWorkHours,
} from "../../../services/workHoursService";
import { Base } from "../../common/base";

export interface WorkHoursState {
    normal: IWorkHoursItem[];
    calculated: IWorkHoursItem[];
}

const initialState: WorkHoursState = {
    normal: [],
    calculated: [],
};

export const workHoursSlice = createSlice({
    name: "workHours",
    initialState,
    reducers: {
        workHoursReceived: (
            state,
            action: PayloadAction<{
                workHours: IWorkHoursItem[];
                startDate: string;
                endDate: string;
                normalOnly: boolean;
            }>
        ) => {
            let items = action.payload.normalOnly
                ? state.normal
                : state.calculated;

            // Preserve existing items outside of given range
            items = items.filter(
                (h) =>
                    h.dateStr < action.payload.startDate ||
                    h.dateStr > action.payload.endDate
            );
            items = [...items, ...action.payload.workHours];

            if (action.payload.normalOnly) {
                state.normal = items;
            } else {
                state.calculated = items;
            }
        },
    },
});

export const { workHoursReceived } = workHoursSlice.actions;

export const normalWorkHours = createAsyncThunk(
    "workHours/normalWorkHours",
    async(
        { startDate, endDate }: { startDate: string; endDate: string },
        thunkApi
    ) => {
        const workHoursItems = await getUpToDateNormalWorkHours(
            startDate,
            endDate,
            thunkApi.signal
        );
        thunkApi.dispatch(
            workHoursReceived({
                workHours: workHoursItems,
                startDate,
                endDate,
                normalOnly: true,
            })
        );
    }
);

export const calculatedWorkHours = createAsyncThunk(
    "workHours/calculatedWorkHours",
    async(
        {
            salaryPeriodId,
            startDate,
            endDate,
        }: { salaryPeriodId: string; startDate: string; endDate: string },
        thunkApi
    ) => {
        const workHoursItems = await getUpToDateWorkHours(
            salaryPeriodId,
            Base.isAfter(Base.dateStartOf(new Date(), "day"), endDate),
            thunkApi.signal
        );
        thunkApi.dispatch(
            workHoursReceived({
                workHours: workHoursItems,
                startDate,
                endDate,
                normalOnly: false,
            })
        );
    }
);

export const workHoursReducer = workHoursSlice.reducer;

const makeSelectWorkHoursByDate = (normal: boolean) =>
    createSelector(
        (state: WorkHoursState) => (normal ? state.normal : state.calculated),
        (_, rangeStart: string) => rangeStart,
        (_, _rangeStart, rangeEnd: string) => rangeEnd,
        (_, _rangeStart, _rangeEnd: string, categories?: string[]) =>
            categories,
        (
            _,
            _rangeStart,
            _rangeEnd,
            _categories?,
            withoutShiftIdOnly?: boolean
        ) => withoutShiftIdOnly,
        (
            items: IWorkHoursItem[],
            rangeStart: string,
            rangeEnd: string,
            categories: string[],
            withoutShiftIdOnly: boolean
        ) => {
            if (rangeStart && rangeEnd) {
                return items.filter(
                    (i) =>
                        (!withoutShiftIdOnly || !i.workShiftId) &&
                        (!categories || categories.includes(i.category)) &&
                        Base.isBetween(
                            i.dateStr,
                            rangeStart,
                            rangeEnd,
                            "day",
                            "[]"
                        )
                );
            }
        }
    );

const makeSelectWorkHoursByWorkShiftIds = (normal: boolean) =>
    createSelector(
        (state: WorkHoursState) => (normal ? state.normal : state.calculated),
        (_, workShiftIds: string[]) => workShiftIds,
        (_, _workShiftIds, categories?: string[]) => categories,
        (_, _workShiftIds, _categories, salaryRowTypeId?: string) =>
            salaryRowTypeId,
        (
            items: IWorkHoursItem[],
            workShiftIds: string[],
            categories: string[],
            salaryRowTypeId: string
        ) => {
            if (workShiftIds?.length > 0) {
                return items.filter(
                    (i) =>
                        workShiftIds.includes(i.workShiftId) &&
                        (!categories || categories.includes(i.category)) &&
                        (!salaryRowTypeId ||
                            salaryRowTypeId === i.salaryRowTypeId)
                );
            }
        }
    );

const makeSelectWorkHoursAmountByDate = (normal: boolean) =>
    createSelector([makeSelectWorkHoursByDate(normal)], (items) =>
        items ? items.reduce((acc, cur) => acc + cur.amount, 0) : undefined
    );

const makeSelectWorkHoursAmountByWorkShiftIds = (normal: boolean) =>
    createSelector([makeSelectWorkHoursByWorkShiftIds(normal)], (items) =>
        items ? items.reduce((acc, cur) => acc + cur.amount, 0) : undefined
    );

const makeSelectWorkHoursByWorkShiftIdsGroupedByCategory = (normal: boolean) =>
    createSelector([makeSelectWorkHoursByWorkShiftIds(normal)], (items) =>
        Base.groupArray(items, "categoryId")
    );

const makeSelectWorkHoursByDateGroupedByWorkShiftIdAndDate = (
    normal: boolean,
) =>
    createSelector([makeSelectWorkHoursByDate(normal)], (items) => {
        const groupedByShift = Base.groupArray(items, "workShiftId");

        return Object.fromEntries(
            Object.keys(groupedByShift).map((k) => [
                k,
                Base.groupArray(groupedByShift[k], "dateStr"),
            ]),
        );
    });

const makeSelectCalculatedWorkHoursByDateGroupedBySalaryRowTypeId = () =>
    createSelector([makeSelectWorkHoursByDate(false)], (items) =>
        Base.groupArray(items, "salaryRowTypeId")
    );

const makeSelectWorkHoursAmountByWorkShiftIdsGroupedByCategory = (
    normal: boolean
) =>
    createSelector(
        [makeSelectWorkHoursByWorkShiftIdsGroupedByCategory(normal)],
        (grouped) =>
            Object.fromEntries(
                Object.keys(grouped).map((categoryId) => [
                    categoryId,
                    grouped[categoryId]?.reduce(
                        (acc, cur) => acc + cur.amount,
                        0
                    ),
                ])
            )
    );

const makeSelectWorkHoursAmountByDateGroupedByWorkShiftIdAndDate = (
    normal: boolean,
) =>
    createSelector(
        [makeSelectWorkHoursByDateGroupedByWorkShiftIdAndDate(normal)],
        (grouped) =>
            Object.fromEntries(
                Object.keys(grouped).map((shiftId) => [
                    shiftId,
                    Object.fromEntries(
                        Object.keys(grouped[shiftId]).map((date) => [
                            date,
                            grouped[shiftId][date]?.reduce(
                                (acc, cur) => acc + cur.amount,
                                0,
                            ),
                        ]),
                    ),
                ]),
            ),
    );

export const makeSelectCalculatedWorkHoursAmountByDateGroupedBySalaryRowTypeId =
    () =>
        createSelector(
            [makeSelectCalculatedWorkHoursByDateGroupedBySalaryRowTypeId()],
            (grouped) =>
                Object.fromEntries(
                    Object.keys(grouped).map((salaryRowTypeName) => [
                        salaryRowTypeName,
                        grouped[salaryRowTypeName]?.reduce(
                            (acc, cur) => acc + cur.amount,
                            0
                        ),
                    ])
                )
        );

export const makeSelectNormalWorkHoursByDate = () =>
    makeSelectWorkHoursByDate(true);
export const makeSelectCalculatedWorkHoursByDate = () =>
    makeSelectWorkHoursByDate(false);

export const makeSelectNormalWorkHoursAmountByDate = () =>
    makeSelectWorkHoursAmountByDate(true);
export const makeSelectCalculatedWorkHoursAmountByDate = () =>
    makeSelectWorkHoursAmountByDate(false);

export const makeSelectNormalWorkHoursAmountByDateGroupedByWorkShiftIdAndDate =
    () => makeSelectWorkHoursAmountByDateGroupedByWorkShiftIdAndDate(true);
export const makeSelectCalculatedWorkHoursAmountByDateGroupedByWorkShiftIdAndDate =
    () => makeSelectWorkHoursAmountByDateGroupedByWorkShiftIdAndDate(false);

export const makeSelectNormalWorkHoursAmountByWorkShiftIdsGroupedByCategory =
    () => makeSelectWorkHoursAmountByWorkShiftIdsGroupedByCategory(true);
export const makeSelectCalculatedWorkHoursAmountByWorkShiftIdsGroupedByCategory =
    () => makeSelectWorkHoursAmountByWorkShiftIdsGroupedByCategory(false);

export const makeSelectNormalWorkHoursByWorkShiftIds = () =>
    makeSelectWorkHoursByWorkShiftIds(true);
export const makeSelectCalculatedWorkHoursByWorkShiftIds = () =>
    makeSelectWorkHoursByWorkShiftIds(false);

export const makeSelectNormalWorkHoursAmountByWorkShiftIds = () =>
    makeSelectWorkHoursAmountByWorkShiftIds(true);
export const makeSelectCalculatedWorkHoursAmountByWorkShiftIds = () =>
    makeSelectWorkHoursAmountByWorkShiftIds(false);